import { parseTimeInput } from '../../utils/timeInput';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  addDays, addMonths, clampDateTime, dayOf, defaultTime, gregorianMonthLabel, hebrewDay, hebrewMonthLabel,
  isDayDisabled, isDayKey, joinDateTime, monthCells, placePopover, startMonth, timeOf, toKey, weekdayLabels,
} from './datePickerModel';

/**
 * P-52 (D-225 sec.1 / QX-440) - ONE date picker for every `<input type="date">`
 * in the system. Mounted once in the shell (like AutoTooltip); it listens on the
 * document, so no form has to change and no form can be missed:
 *  - a click ANYWHERE on the field (not just the icon) opens the calendar;
 *  - the calendar shows the small Hebrew date under each Gregorian day;
 *  - the value is written through the native setter and an `input`/`change`
 *    event, so React-controlled and uncontrolled fields both see it;
 *  - min / max / disabled / readOnly of the field are respected;
 *  - focus stays on the field (typing the date still works): the arrows move
 *    the day, PageUp/PageDown the month, Enter picks, Escape closes.
 * A field opts out with `data-native-date`.
 * P-57c (D-232 sec.4 / QX-611) - the same layer also serves `datetime-local`
 * fields (ui/DateTimeInput): the same calendar plus an hour / minute column.
 * The value written is the same `YYYY-MM-DDTHH:mm` the browser field holds, so
 * what the server receives does not change.
 */

const SELECTOR = 'input[type="date"], input[type="datetime-local"]';
const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

function dateInputOf(t: EventTarget | null): HTMLInputElement | null {
  const el = t instanceof Element ? t.closest<HTMLInputElement>(SELECTOR) : null;
  if (!el || el.disabled || el.readOnly || el.hasAttribute('data-native-date')) return null;
  return el;
}

function writeValue(input: HTMLInputElement, value: string) {
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
  setter?.call(input, value);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

export default function DatePickerLayer() {
  const { t, i18n } = useTranslation();
  const [input, setInput] = useState<HTMLInputElement | null>(null);
  const [cursor, setCursor] = useState('');
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [timeDraftInvalid, setTimeDraftInvalid] = useState(false);
  const [typedTime, setTypedTime] = useState<string | null>(null); // what is typed in the time box; null = show the field's time
  const [, bump] = useState(0); // the field's value changed while the panel stays open (date+time)
  const panel = useRef<HTMLDivElement | null>(null);
  const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'he-IL';
  const today = toKey(new Date());

  const close = useCallback(() => { setInput(null); setPos(null); setTimeDraftInvalid(false); setTypedTime(null); }, []);

  const open = useCallback((el: HTMLInputElement) => {
    setCursor(startMonth(dayOf(el.value), toKey(new Date()), dayOf(el.min), dayOf(el.max)));
    setPos(null);
    setInput(el);
  }, []);

  const withTime = input?.type === 'datetime-local';

  const pick = useCallback((key: string, keepOpen = false) => {
    if (!input || isDayDisabled(key, dayOf(input.min), dayOf(input.max))) return;
    if (input.type === 'datetime-local') {
      writeValue(input, clampDateTime(joinDateTime(key, defaultTime(input.value, new Date())), input.min, input.max));
      bump((n) => n + 1);
      input.focus({ preventScroll: true });
      if (!keepOpen) close();
      return;
    }
    writeValue(input, key);
    input.focus({ preventScroll: true });
    close();
  }, [input, close]);

  const pickTime = useCallback((hh: string | null, mm: string | null, noFocus = false) => {
    if (!input) return;
    const day = dayOf(input.value) || toKey(new Date());
    const [h0, m0] = defaultTime(input.value, new Date()).split(':');
    writeValue(input, clampDateTime(joinDateTime(day, `${hh ?? h0}:${mm ?? m0}`), input.min, input.max));
    bump((n) => n + 1);
    // typing in the time box must keep the focus there (D-292b); list picks still return it to the field
    if (!noFocus) input.focus({ preventScroll: true });
  }, [input]);

  // Open on a click anywhere on the field. preventDefault stops the browser's own picker.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const el = dateInputOf(e.target);
      if (!el) return;
      e.preventDefault();
      if (el === input) { close(); return; }
      el.focus({ preventScroll: true });
      open(el);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [input, open, close]);

  // While open: outside press / scroll / resize close it; the keyboard drives it from the field.
  useEffect(() => {
    if (!input) return;
    const onDown = (e: PointerEvent) => {
      const tg = e.target as Node;
      if (panel.current?.contains(tg) || input.contains(tg)) return;
      close();
    };
    const onScroll = (e: Event) => { if (!panel.current?.contains(e.target as Node)) close(); };
    const onKey = (e: KeyboardEvent) => {
      if (e.target !== input) return;
      const stop = () => { e.preventDefault(); e.stopPropagation(); };
      const base = isDayKey(cursor) ? cursor : today;
      const move = (key: string) => { stop(); setCursor(key); };
      if (e.key === 'Escape') { stop(); close(); }
      else if (e.key === 'Tab') close();
      else if (e.key === 'Enter') { stop(); pick(cursor); } // date+time: Enter takes the day and the time already typed
      else if (e.key === 'ArrowLeft') move(addDays(base, document.dir === 'rtl' ? 1 : -1));
      else if (e.key === 'ArrowRight') move(addDays(base, document.dir === 'rtl' ? -1 : 1));
      else if (e.key === 'ArrowUp') move(addDays(base, -7));
      else if (e.key === 'ArrowDown') move(addDays(base, 7));
      else if (e.key === 'PageUp') move(addMonths(base, -1));
      else if (e.key === 'PageDown') move(addMonths(base, 1));
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [input, cursor, today, close, pick]);

  // Position once the panel has a size.
  useLayoutEffect(() => {
    if (!input || !panel.current) return;
    const r = input.getBoundingClientRect();
    const box = panel.current.getBoundingClientRect();
    setPos(placePopover(r, { width: box.width, height: box.height }, { width: window.innerWidth, height: window.innerHeight }));
  }, [input]);

  const pickNow = useCallback(() => {
    if (!input) return;
    const n = new Date();
    writeValue(input, clampDateTime(joinDateTime(toKey(n), `${String(n.getHours()).padStart(2, '0')}:${String(n.getMinutes()).padStart(2, '0')}`), input.min, input.max));
    bump((x) => x + 1);
    close();
  }, [input, close]);

  // a pick from the list (not the echo of what was typed) puts the field's time back in the box
  const fieldTime = input ? timeOf(input.value) : '';
  useEffect(() => { setTypedTime((d) => (d !== null && parseTimeInput(d) === fieldTime ? d : null)); }, [fieldTime]);

  const cells = useMemo(() => (cursor ? monthCells(cursor) : []), [cursor]);
  const weekdays = useMemo(() => weekdayLabels(locale), [locale]);
  if (!input) return null;

  const selected = input.value;
  const nav = 'inline-flex h-8 w-8 items-center justify-center rounded-md text-text-2 hover:bg-surface-subtle hover:text-text';
  const keep = (e: React.MouseEvent) => e.preventDefault(); // keep focus on the field

  return createPortal(
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- mousedown only preserves focus on the field; keyboard handled by the field
    <div
      ref={panel}
      role="dialog"
      aria-label={withTime ? t('datePicker.labelTime', 'בחירת תאריך ושעה') : t('datePicker.label', 'בחירת תאריך')}
      dir={document.dir === 'ltr' ? 'ltr' : 'rtl'}
      onMouseDown={keep}
      style={{ position: 'fixed', top: pos?.top ?? 0, left: pos?.left ?? 0, visibility: pos ? 'visible' : 'hidden', width: 288 }}
      className="z-toast rounded-xl border border-border bg-surface p-3 text-text shadow-lg"
    >
      <div className="mb-2 flex items-center justify-between">
        <button type="button" className={nav} aria-label={t('datePicker.prevMonth', 'חודש קודם')} onClick={() => setCursor(addMonths(cursor, -1))}>
          <span aria-hidden="true">{document.dir === 'ltr' ? '‹' : '›'}</span>
        </button>
        <div className="text-center leading-tight" aria-live="polite">
          <div className="text-sm font-semibold">{gregorianMonthLabel(cursor, locale)}</div>
          <div className="text-xs text-text-2">{hebrewMonthLabel(cursor)}</div>
        </div>
        <button type="button" className={nav} aria-label={t('datePicker.nextMonth', 'חודש הבא')} onClick={() => setCursor(addMonths(cursor, 1))}>
          <span aria-hidden="true">{document.dir === 'ltr' ? '›' : '‹'}</span>
        </button>
      </div>
      <div className="grid grid-cols-7 text-center text-xs text-text-2" aria-hidden="true">
        {weekdays.map((w, i) => <div key={i} className="py-1">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-0.5" role="grid">
        {cells.map((key, i) => {
          if (!key) return <div key={`b${i}`} />;
          const off = isDayDisabled(key, dayOf(input.min), dayOf(input.max));
          const isSel = key === dayOf(selected);
          const isCur = key === cursor;
          return (
            <button
              key={key}
              type="button"
              disabled={off}
              aria-pressed={isSel}
              aria-current={key === today ? 'date' : undefined}
              onClick={() => pick(key, withTime)}
              className={`flex h-11 flex-col items-center justify-center rounded-md leading-none transition-colors duration-micro ${
                isSel ? 'bg-accent text-white'
                  : off ? 'cursor-not-allowed text-text-2 opacity-40'
                    : `hover:bg-surface-subtle ${key === today ? 'border border-accent' : ''} ${isCur ? 'ring-1 ring-accent' : ''}`
              }`}
            >
              <span className="text-sm">{Number(key.slice(8))}</span>
              <span className={`mt-0.5 whitespace-nowrap text-xs leading-none ${isSel ? 'text-white/80' : 'text-text-2'}`} title={hebrewDay(key)}>
                {/* the 1st's month name doesn't fit a cell at 12px; the header already names both months */}
                {hebrewDay(key).split(' ')[0]}
              </span>
            </button>
          );
        })}
      </div>
      {withTime && (
        <div className="mt-2 flex items-start gap-2 border-t border-border pt-2" dir="ltr" data-testid="datetime-time-columns">
          <span className="pt-1 text-xs text-text-2">{t('datePicker.time', 'שעה')}</span>
          {/* D-292: the time can also be typed straight in (0930 / 930 / 9:30 / 09:30) */}
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            maxLength={5}
            dir="ltr"
            data-testid="datetime-time-typed"
            aria-label={t('datePicker.typeTime', 'הקלדת שעה')}
            placeholder="HH:mm"
            value={typedTime ?? timeOf(selected)}
            aria-invalid={timeDraftInvalid || undefined}
            className={`input h-7 w-16 px-1 py-0 text-center text-sm tabular-nums ${timeDraftInvalid ? 'input-error' : ''}`}
            onMouseDown={(e) => e.stopPropagation()}
            onChange={(e) => {
              setTypedTime(e.target.value);
              const p = parseTimeInput(e.target.value);
              setTimeDraftInvalid(e.target.value.trim() !== '' && p === null);
              if (p) { const [h, m] = p.split(':'); pickTime(h, m, true); }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); if (!timeDraftInvalid) close(); }
              else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
            }}
          />
          <div className="max-h-28 flex-1 overflow-y-auto rounded-md border border-border" role="listbox" aria-label={t('datePicker.hour', 'שעות')}>
            {HOURS.map((h) => (
              <button key={h} type="button" role="option" aria-selected={timeOf(selected).slice(0, 2) === h}
                onClick={() => pickTime(h, null)}
                className={`block w-full px-2 py-1 text-center text-sm ${timeOf(selected).slice(0, 2) === h ? 'bg-accent text-white' : 'hover:bg-surface-subtle'}`}>{h}</button>
            ))}
          </div>
          <div className="max-h-28 flex-1 overflow-y-auto rounded-md border border-border" role="listbox" aria-label={t('datePicker.minute', 'דקות')}>
            {MINUTES.map((m) => (
              <button key={m} type="button" role="option" aria-selected={timeOf(selected).slice(3, 5) === m}
                onClick={() => pickTime(null, m)}
                className={`block w-full px-2 py-1 text-center text-sm ${timeOf(selected).slice(3, 5) === m ? 'bg-accent text-white' : 'hover:bg-surface-subtle'}`}>{m}</button>
            ))}
          </div>
        </div>
      )}
      <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-sm">
        <button type="button" className="rounded-md px-2 py-1 text-accent hover:bg-surface-subtle" onClick={() => (withTime ? pickNow() : pick(today))} disabled={isDayDisabled(today, dayOf(input.min), dayOf(input.max))}>
          {withTime ? t('datePicker.now', 'עכשיו') : t('datePicker.today', 'היום')}
        </button>
        {withTime && (
          <button type="button" className="rounded-md bg-accent px-3 py-1 text-white" onClick={close}>
            {t('datePicker.done', 'אישור')}
          </button>
        )}
        {!input.required && selected && (
          <button type="button" className="rounded-md px-2 py-1 text-text-2 hover:bg-surface-subtle" onClick={() => { writeValue(input, ''); close(); }}>
            {t('datePicker.clear', 'ניקוי')}
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}
